/**
 * CloudParco — the installer's fleet, read from the ema-comfort API. The cloud
 * sibling of SimulatorParco: `listaImpianti()` is the register (`GET /v1/plants`)
 * and `clientPer(id)` hands back a CloudClient for one plant.
 *
 * The register does NOT track live device state — connection, alarms, zone and
 * generator counts are the device plane's, which is unbuilt. So the fleet list
 * carries those as neutral/unknown here (never a fabricated "connesso"); the
 * per-plant view is corrected by the plant's own readings once opened. This is
 * the same honesty the backend `plant_summary` keeps.
 */

import type { ImpiantoSintesi } from '@emacomfort/domain';
import { CloudClient } from './CloudClient';
import type { OpzioniCloud } from './CloudClient';
import type { ImpiantoRegistro } from './tipi';

type OpzioniParco = Pick<OpzioniCloud, 'baseUrl' | 'getToken'> &
  Partial<Pick<OpzioniCloud, 'fetch' | 'intervalloMs' | 'orologio'>>;

interface RispostaFleet {
  plants: Array<
    Pick<ImpiantoRegistro, 'id' | 'tenantId' | 'nome' | 'cliente'> & {
      citta?: string;
      tipoCentralina?: string;
      firmware?: string;
    }
  >;
}

export class CloudParco {
  private readonly fetch: typeof globalThis.fetch;

  constructor(private readonly opz: OpzioniParco) {
    this.fetch = opz.fetch ?? globalThis.fetch.bind(globalThis);
  }

  async listaImpianti(): Promise<ImpiantoSintesi[]> {
    const body = await this.richiesta<RispostaFleet>('/v1/plants');
    return (body.plants ?? []).map((p) => ({
      id: p.id,
      tenantId: p.tenantId,
      nome: p.nome,
      citta: p.citta ?? '',
      cliente: p.cliente,
      tipoCentralina: p.tipoCentralina === 'terze_parti' ? 'terze_parti' : 'emacomfort',
      // Live fields the register does not track — unknown until the plant is
      // opened and its readings arrive. Never a fabricated "connesso".
      connessione: 'irraggiungibile',
      ultimoContatto: null,
      allarmiAperti: 0,
      allarmeMassimo: null,
      zone: 0,
      generatori: 0,
      firmware: p.firmware ?? '',
    }));
  }

  clientPer(impiantoId: string): CloudClient {
    // 🚨 Si inoltra solo ciò che è stato effettivamente scelto.
    //
    // Elencare le opzioni facoltative una per una sembra innocuo, ma fabbrica
    // chiavi PRESENTI e valorizzate `undefined`, che poi sovrascrivono i
    // predefiniti di chi le riceve. È così che il poll è finito senza
    // intervallo, bombardando l'API finché il browser non ha esaurito le
    // socket. `CloudClient` ora si difende da solo (`??` dopo lo spread), ma
    // la causa era qui: un `undefined` inoltrato è un valore, non un silenzio.
    const facoltative = {
      ...(this.opz.fetch !== undefined && { fetch: this.opz.fetch }),
      ...(this.opz.intervalloMs !== undefined && { intervalloMs: this.opz.intervalloMs }),
      ...(this.opz.orologio !== undefined && { orologio: this.opz.orologio }),
    };
    return new CloudClient({
      impiantoId,
      baseUrl: this.opz.baseUrl,
      getToken: this.opz.getToken,
      ...facoltative,
    });
  }

  private async richiesta<T>(percorso: string): Promise<T> {
    const token = await this.opz.getToken();
    const resp = await this.fetch(`${this.opz.baseUrl}${percorso}`, {
      headers: { authorization: `Bearer ${token}`, accept: 'application/json' },
    });
    if (!resp.ok) throw new Error(`api_${resp.status}`);
    return (await resp.json()) as T;
  }
}
